// src/data/languageService.js
//
// Reads the platform's languages and each country's default, and reads and writes a member's own
// language choice (Roadmap #85, docs/briefs/2026-10-01-country-default-language-addendum-2.md).
// Tables: platform_languages, country_default_languages, users.preferred_locale
// (supabase/migrations/20261001_platform_languages.sql). Everyone may read the two tables; only a
// platform admin writes them, and no screen does yet.

import { supabaseFetch } from '../lib/supabase';

/**
 * The offered languages, in menu order, and each country's default.
 * @returns {Promise<{ languages: Array<{locale: string, native_name: string}>, countryDefaults: Object<string, string> } | null>}
 *   null when either read failed — the caller must treat that as "unknown", not "none offered".
 */
export async function getLanguageSettings() {
  const [languagesResult, defaultsResult] = await Promise.all([
    supabaseFetch.query('platform_languages', {
      select: 'locale,native_name',
      eq: { is_offered: true },
      order: 'display_order.asc,locale.asc'
    }),
    supabaseFetch.query('country_default_languages', {
      select: 'country_code,locale'
    })
  ]);

  if (languagesResult.error || defaultsResult.error) {
    console.error('Failed to load language settings:', languagesResult.error || defaultsResult.error);
    return null;
  }

  const countryDefaults = {};
  (defaultsResult.data || []).forEach(row => {
    countryDefaults[row.country_code.trim()] = row.locale;
  });

  return { languages: languagesResult.data || [], countryDefaults };
}

/**
 * The member's own chosen language. The viewer's own account lives in public even while a demo
 * admin is viewing a demo group (demo.users has no preferred_locale), hence schema: 'public'.
 * @param {string} userId
 * @returns {Promise<{ locale: string|null, error: any }>} a failed read is reported as an error,
 *   never as "no choice" — the caller must not mistake one for the other.
 */
export async function getMyPreferredLocale(userId) {
  const { data, error } = await supabaseFetch.query('users', {
    select: 'preferred_locale',
    eq: { id: userId },
    single: true,
    schema: 'public'
  });
  if (error || !data) return { locale: null, error: error || { message: 'No user row returned' } };
  return { locale: data.preferred_locale || null, error: null };
}

/**
 * Records the member's own chosen language on their account.
 * @param {string} userId
 * @param {string} locale - must be a platform_languages locale (the database refuses any other)
 * @returns {Promise<{ error: any }>}
 */
export async function setMyPreferredLocale(userId, locale) {
  const { error } = await supabaseFetch.update('users', { preferred_locale: locale }, {
    eq: { id: userId },
    schema: 'public'
  });
  return { error: error || null };
}
